"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useMemo } from "react";
import { Users, Clock, Calendar, DollarSign } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ExperienceFilterConsumer() {
  const {
    posts: services,
    loading,
    refetch
  } = usePosts();
  const [selectedOccasion, setSelectedOccasion] = useState<string>("all");
  const [selectedGroupSize, setSelectedGroupSize] = useState<string>("all");
  const [selectedDuration, setSelectedDuration] = useState<string>("all");
  const [selectedActivityType, setSelectedActivityType] = useState<string>("all");
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    try {
      const response = await fetch(`/wp-json/wp/v2/service/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': (window as any).wpApiSettings?.nonce || ''
        }
      });
      if (response.ok) {
        setDeletedIds(prev => [...prev, id]);
      } else {
        alert("Failed to delete the service. Please check your permissions.");
      }
    } catch (error) {
      console.error("Error deleting service:", error);
      alert("An error occurred while trying to delete the service.");
    }
  };
  const handleReorder = async (id: string, currentOrder: number, direction: 'up' | 'down') => {
    const newOrder = direction === 'up' ? Math.max(0, currentOrder - 1) : currentOrder + 1;
    try {
      const response = await fetch(`/wp-json/wp/v2/service/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': (window as any).wpApiSettings?.nonce || ''
        },
        body: JSON.stringify({
          meta: {
            service_order: newOrder
          }
        })
      });
      if (response.ok) {
        await refetch();
      } else {
        alert("Failed to update order.");
      }
    } catch (error) {
      console.error("Error reordering service:", error);
      alert("An error occurred while trying to reorder.");
    }
  };
  const sortedServices = useMemo(() => {
    return [...services].filter(s => !deletedIds.includes(s.id)).sort((a, b) => {
      const orderA = parseInt(a.customFields?.service_order || "999", 10);
      const orderB = parseInt(b.customFields?.service_order || "999", 10);
      return orderA - orderB;
    });
  }, [services, deletedIds]);
  const occasions = ['Social / Networking', 'Team Building', 'Incentive Trip', 'Sightseeing'];
  const groupSizes = useMemo(() => Array.from(new Set(sortedServices.map(s => s.customFields?.group_size).filter(Boolean))), [sortedServices]);
  const durations = useMemo(() => Array.from(new Set(sortedServices.map(s => s.customFields?.duration).filter(Boolean))), [sortedServices]);
  const activityTypes = ['Food', 'Wellness', 'Cultural', 'Active', 'Orientation'];
  const filteredServices = sortedServices.filter(service => {
    const matchOccasion = selectedOccasion === "all" || service.customFields?.occasion === selectedOccasion;
    const matchGroupSize = selectedGroupSize === "all" || service.customFields?.group_size === selectedGroupSize;
    const matchDuration = selectedDuration === "all" || service.customFields?.duration === selectedDuration;
    const matchActivityType = selectedActivityType === "all" || service.customFields?.activity_type === selectedActivityType;
    return matchOccasion && matchGroupSize && matchDuration && matchActivityType;
  });
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-12 animate-pulse">
        {[...Array(3)].map((_, i) => <div key={i} className="h-96 bg-muted rounded-2xl" />)}
      </div>;
  }
  const placeholders = ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638249/img/service_placeholder_1.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638249/img/service_placeholder_2.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638249/img/service_placeholder_3.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638249/img/service_placeholder_4.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638249/img/service_placeholder_5.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638249/img/service_placeholder_6.png"];
  return <>
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        <Select value={selectedOccasion} onValueChange={setSelectedOccasion}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Occasion" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Occasions</SelectItem>
            {occasions.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={selectedGroupSize} onValueChange={setSelectedGroupSize}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Group Size" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sizes</SelectItem>
            {groupSizes.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={selectedDuration} onValueChange={setSelectedDuration}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Duration" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Durations</SelectItem>
            {durations.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={selectedActivityType} onValueChange={setSelectedActivityType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Activity Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {activityTypes.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
        {filteredServices.map((service, i) => {
        const imageSrc = service.customFields?.service_image && typeof service.customFields.service_image === 'string' && service.customFields.service_image.trim() !== "" ? service.customFields.service_image : placeholders[i % 6];
        return <Card key={service.id} data-index={i} className={`relative flex-col overflow-hidden shadow-lg border-border/40 rounded-2xl transition-all duration-300 hover:shadow-xl ${i % 2 === 0 ? "bg-card" : "bg-accent/30"}`}>
              <div className="w-full h-64 md:h-80 overflow-hidden shrink-0">
                <img src={imageSrc} data-wvc-srcset="" data-wvc-sizes="" alt={service.customFields?.service_title || service.title || "Service image"} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
              </div>
              <CardContent className="space-y-6 pb-8">
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {service.customFields?.activity_type && <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-md bg-primary/10 text-primary">
                        {service.customFields.activity_type}
                      </span>}
                    {service.customFields?.occasion && <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-md bg-secondary/10 text-secondary">
                        {service.customFields.occasion}
                      </span>}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-semibold text-primary font-serif">
                    {service.customFields?.service_title || service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed font-sans text-lg">
                    {service.customFields?.service_description || service.excerpt}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-6 pt-6 border-t border-border/50">
                  {service.customFields?.duration && <div className="flex items-center gap-3 text-sm text-muted-foreground font-sans">
                      <div className="p-2 rounded-lg bg-primary/5 text-primary">
                        <Clock className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] uppercase tracking-tight font-bold opacity-60">Duration</span>
                        <span className="font-medium text-foreground">{service.customFields.duration}</span>
                      </div>
                    </div>}
                  {service.customFields?.capacity && <div className="flex items-center gap-3 text-sm text-muted-foreground font-sans">
                      <div className="p-2 rounded-lg bg-primary/5 text-primary">
                        <Users className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] uppercase tracking-tight font-bold opacity-60">Capacity</span>
                        <span className="font-medium text-foreground">{service.customFields.capacity}</span>
                      </div>
                    </div>}
                  {service.customFields?.time_of_day && <div className="flex items-center gap-3 text-sm text-muted-foreground font-sans">
                      <div className="p-2 rounded-lg bg-primary/5 text-primary">
                        <SafeHydrate><Calendar className="w-4 h-4" /></SafeHydrate>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] uppercase tracking-tight font-bold opacity-60">Best Time</span>
                        <span className="font-medium text-foreground">{service.customFields.time_of_day}</span>
                      </div>
                    </div>}
                  {service.customFields?.price_per_head && <div className="flex items-center gap-3 text-sm text-muted-foreground font-sans">
                      <div className="p-2 rounded-lg bg-primary/5 text-primary">
                        <DollarSign className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] uppercase tracking-tight font-bold opacity-60">Pricing</span>
                        <span className="font-medium text-foreground">{service.customFields.price_per_head}</span>
                      </div>
                    </div>}
                </div>
                <Link to={`/post/service/${service.id}`} className="inline-flex items-center justify-center w-full h-10 px-6 rounded-lg bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 font-sans font-semibold tracking-wide transition-all">
                  More
                </Link>
              </CardContent>
            </Card>;
      })}
      </div>

      {filteredServices.length === 0 && !loading && <div className="text-center py-20">
          <p className="text-muted-foreground text-lg font-sans">No services match your criteria. Please try adjusting your filters.</p>
        </div>}
    </>;
}
export default function Experiencefilter() {
  const wp_query_params = {
    post_type: "service",
    per_page: 100,
    status: "publish",
    orderby: "title",
    order: "asc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="260649" id="experiencefilter" className="py-20 bg-background text-foreground">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-primary font-serif mb-4">
            Tailored Corporate Experiences
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-sans">
            On-the-ground expertise for high-quality professional programs in Hong Kong.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "service",
        per_page: 100,
        status: ["publish"],
        orderby: "title",
        order: "asc"
      }}>
          <ExperienceFilterConsumer />
        </PostsProvider>
      </div>
    </section>;
}